import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import RefreshIcon from '@mui/icons-material/Refresh';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PreviewIcon from '@mui/icons-material/Preview';
import { AiGradientButton } from '../../shared';
import {
  getHotelCommunicationSettings,
  getRetentionCampaign,
  isCommEventEnabled,
  previewRetentionMessage,
  sendCustomerOfferMessage,
} from '../../services/api';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';

const ACCENT = DASHBOARD_ACCENT;
const INACTIVE_OPTIONS = [
  { value: 60, label: '60+ days inactive' },
  { value: 90, label: '90+ days inactive' },
  { value: 120, label: '120+ days inactive' },
  { value: 180, label: '180+ days inactive' },
];

const SEGMENT_ICONS = {
  vip_champion: StarIcon,
  high_value: DiamondIcon,
  loyal_regular: FavoriteIcon,
  win_back: ReplayIcon,
};

const STEPS = ['Identify guests', 'Select recipients', 'Preview & send'];

function StatCard({ label, value, sub, icon: Icon, isDark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha(ACCENT, 0.18)}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#1a1e28', 0.98)} 0%, ${alpha('#12151c', 0.98)} 100%)`
          : 'linear-gradient(145deg, #ffffff 0%, #f0fdf9 100%)',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(ACCENT, isDark ? 0.14 : 0.12),
            color: ACCENT,
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {label}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.2, mt: 0.25 }}>
            {value}
          </Typography>
          {sub ? (
            <Typography variant="caption" color="text.secondary">
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

export function AiRetentionPanel({ active = true, onClose, variant = 'drawer' }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isPage = variant === 'page';

  const [step, setStep] = useState(0);
  const [inactiveDays, setInactiveDays] = useState(90);
  const [loading, setLoading] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [selected, setSelected] = useState({});
  const [segmentFilter, setSegmentFilter] = useState('all');
  const [segmentTemplates, setSegmentTemplates] = useState({});
  const [previewGuestId, setPreviewGuestId] = useState(null);
  const [previewTab, setPreviewTab] = useState('email');
  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [whatsappPreview, setWhatsappPreview] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [channels, setChannels] = useState({ email: true, whatsapp: false });
  const [commSettings, setCommSettings] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');

  const canEmail = isCommEventEnabled(commSettings, 'customer_offer', 'email');
  const canWhatsApp = isCommEventEnabled(commSettings, 'customer_offer', 'whatsapp');

  const guests = campaign?.guests || [];
  const segments = campaign?.segments || [];

  const filteredGuests = useMemo(() => {
    if (segmentFilter === 'all') return guests;
    return guests.filter((g) => g.segment_id === segmentFilter);
  }, [guests, segmentFilter]);

  const selectedGuests = useMemo(
    () => guests.filter((g) => selected[g.id]),
    [guests, selected],
  );

  const previewGuest = useMemo(() => {
    if (previewGuestId) {
      return guests.find((g) => g.id === previewGuestId) || selectedGuests[0] || guests[0];
    }
    return selectedGuests[0] || guests[0] || null;
  }, [guests, previewGuestId, selectedGuests]);

  const messageForGuest = useCallback(
    (guest) => {
      const tpl = segmentTemplates[guest.segment_id] || guest.suggested_message || '';
      if (tpl && tpl.includes('{')) {
        return tpl
          .replace(/\{guest_name\}/g, guest.name || 'Guest')
          .replace(/\{hotel_name\}/g, campaign?.hotel_name || 'our hotel')
          .replace(/\{last_stay\}/g, guest.last_booking || '—')
          .replace(/\{total_bookings\}/g, String(guest.total_bookings || 0))
          .replace(/\{total_spent\}/g, `${guest.currency || 'INR'} ${(guest.total_spent || 0).toLocaleString('en-IN')}`)
          .replace(/\{tier_label\}/g, guest.loyalty_tier || 'Valued Guest')
          .replace(/\{offer_hint\}/g, segments.find((s) => s.id === guest.segment_id)?.offer_hint || 'a special offer');
      }
      return tpl || guest.suggested_message || '';
    },
    [campaign?.hotel_name, segmentTemplates, segments],
  );

  const loadCampaign = useCallback(async () => {
    setLoading(true);
    setError('');
    setStatusMsg('');
    try {
      const data = await getRetentionCampaign({ inactive_days: inactiveDays });
      setCampaign(data);
      const templates = {};
      (data.segments || []).forEach((seg) => {
        templates[seg.id] = data.templates?.[seg.id] || seg.default_template || '';
      });
      setSegmentTemplates(templates);
      const nextSel = {};
      (data.guests || []).forEach((g) => {
        nextSel[g.id] = true;
      });
      setSelected(nextSel);
      if (data.guests?.length) {
        setPreviewGuestId(data.guests[0].id);
        setStatusMsg(`AI identified ${data.guests.length} guest(s) across ${data.segments?.filter((s) => s.count > 0).length || 0} behaviour segment(s).`);
      } else {
        setStatusMsg('No lapsed guests found for this window.');
      }
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to identify guests');
      setCampaign(null);
      setSelected({});
    } finally {
      setLoading(false);
    }
  }, [inactiveDays]);

  useEffect(() => {
    if (!active) return;
    getHotelCommunicationSettings()
      .then(setCommSettings)
      .catch(() => setCommSettings(null));
    loadCampaign();
  }, [active, loadCampaign]);

  useEffect(() => {
    if (!active) return;
    setChannels({
      email: canEmail,
      whatsapp: canWhatsApp,
    });
  }, [active, canEmail, canWhatsApp]);

  const refreshPreview = useCallback(async () => {
    if (!previewGuest) return;
    const message = messageForGuest(previewGuest);
    if (!message.trim()) return;
    setPreviewLoading(true);
    try {
      const [emailRes, waRes] = await Promise.all([
        previewRetentionMessage({
          message,
          channel: 'email',
          guest_id: previewGuest.id,
          guest_name: previewGuest.name,
        }),
        previewRetentionMessage({
          message,
          channel: 'whatsapp',
          guest_id: previewGuest.id,
          guest_name: previewGuest.name,
        }),
      ]);
      setEmailPreviewHtml(emailRes?.preview || '');
      setWhatsappPreview(waRes?.preview || '');
    } catch {
      setEmailPreviewHtml('');
      setWhatsappPreview('');
    } finally {
      setPreviewLoading(false);
    }
  }, [messageForGuest, previewGuest]);

  useEffect(() => {
    if (step === 2 && previewGuest) {
      refreshPreview();
    }
  }, [step, previewGuest, previewGuestId, segmentTemplates, refreshPreview]);

  const toggleAll = (checked) => {
    const next = { ...selected };
    filteredGuests.forEach((g) => {
      next[g.id] = checked;
    });
    setSelected(next);
  };

  const handleSend = async () => {
    if (!selectedGuests.length) {
      setError('Select at least one guest.');
      return;
    }
    const sendEmail = channels.email && canEmail;
    const sendWhatsapp = channels.whatsapp && canWhatsApp;
    if (!sendEmail && !sendWhatsapp) {
      setError('Enable Customer offer in Configuration and select email or WhatsApp.');
      return;
    }

    setSending(true);
    setError('');
    let sent = 0;
    const failures = [];

    for (const guest of selectedGuests) {
      const msg = messageForGuest(guest).trim();
      if (!msg) continue;
      try {
        const res = await sendCustomerOfferMessage(guest.id, {
          message: msg,
          send_email: sendEmail && guest.has_email,
          send_whatsapp: sendWhatsapp && guest.has_phone,
        });
        if (res.email_sent || res.whatsapp_sent) sent += 1;
        else failures.push(guest.name);
      } catch {
        failures.push(guest.name);
      }
    }

    setSending(false);
    if (sent > 0) {
      setStatusMsg(`Retention sent to ${sent} of ${selectedGuests.length} guest(s).`);
    }
    if (failures.length) {
      setError(`Could not reach: ${failures.slice(0, 4).join(', ')}${failures.length > 4 ? '…' : ''}`);
    }
  };

  const shellSx = isPage
    ? {}
    : {
        borderRadius: 0,
        overflow: 'hidden',
      };

  return (
    <Box sx={shellSx}>
      {/* Hero */}
      <Box
        sx={
          isPage
            ? { mb: 2, background: 'transparent' }
            : {
                px: 2,
                py: 2,
                mb: 0,
                background: isDark
                  ? `linear-gradient(125deg, ${alpha('#0d4f4a', 0.55)} 0%, ${alpha('#12151c', 0.95)} 48%, ${alpha('#1a1e28', 0.98)} 100%)`
                  : `linear-gradient(125deg, ${alpha(ACCENT, 0.14)} 0%, #ffffff 55%, #f0fdf9 100%)`,
                borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha(ACCENT, 0.12)}`,
              }
        }
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <AutoAwesomeIcon sx={{ color: ACCENT, fontSize: 26 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                AI Retention Campaign
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
              Identify lapsed guests by behaviour, personalize win-back messages per segment, preview email and WhatsApp, then send in one flow.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Lapsed window</InputLabel>
              <Select
                label="Lapsed window"
                value={inactiveDays}
                onChange={(e) => setInactiveDays(Number(e.target.value))}
              >
                {INACTIVE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={loadCampaign}
              disabled={loading}
              sx={{ borderColor: alpha(ACCENT, 0.4), color: ACCENT, whiteSpace: 'nowrap' }}
            >
              Re-scan
            </Button>
          </Stack>
        </Stack>

        {campaign?.summary ? (
          <Grid container spacing={1.5} sx={{ mt: 2.5 }}>
            <Grid item xs={6} sm={3}>
              <StatCard label="Identified" value={campaign.summary.total_identified} sub="AI-scored guests" icon={PeopleOutlineIcon} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Email ready" value={campaign.summary.email_eligible} sub="Has valid email" icon={EmailOutlinedIcon} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="WhatsApp ready" value={campaign.summary.whatsapp_eligible} sub="Has phone number" icon={WhatsAppIcon} isDark={isDark} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard label="Avg score" value={campaign.summary.avg_retention_score} sub="Retention priority" icon={TrendingUpIcon} isDark={isDark} />
            </Grid>
          </Grid>
        ) : null}
      </Box>

      <Box sx={isPage ? { py: 0 } : { px: 2, py: 2 }}>
        <Stepper activeStep={step} alternativeLabel sx={{ mb: 3, display: { xs: 'none', sm: 'flex' } }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert> : null}
        {statusMsg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setStatusMsg('')}>{statusMsg}</Alert> : null}

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <CircularProgress sx={{ color: ACCENT, mb: 2 }} />
            <Typography color="text.secondary">Analyzing guest behaviour and scoring win-back priority…</Typography>
          </Box>
        ) : (
          <>
            {/* Step 0: Segments overview */}
            {step === 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Behaviour segments
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                  {segments.map((seg) => {
                    const SegIcon = SEGMENT_ICONS[seg.id] || ReplayIcon;
                    return (
                      <Grid item xs={12} sm={6} key={seg.id}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: `1px solid ${alpha(seg.color || ACCENT, isDark ? 0.35 : 0.25)}`,
                            bgcolor: isDark ? alpha(seg.color || ACCENT, 0.06) : alpha(seg.color || ACCENT, 0.04),
                          }}
                        >
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Avatar sx={{ bgcolor: alpha(seg.color || ACCENT, 0.15), color: seg.color || ACCENT, width: 36, height: 36 }}>
                              <SegIcon fontSize="small" />
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>{seg.label}</Typography>
                                <Chip label={seg.count} size="small" sx={{ fontWeight: 700, bgcolor: alpha(seg.color || ACCENT, 0.12), color: seg.color || ACCENT }} />
                              </Stack>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                {seg.description}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: seg.color || ACCENT, fontWeight: 600 }}>
                                Offer: {seg.offer_hint}
                              </Typography>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
                <Stack direction="row" justifyContent="flex-end">
                  <Button variant="contained" onClick={() => setStep(1)} disabled={!guests.length} sx={{ bgcolor: ACCENT, color: '#04120e', fontWeight: 700, '&:hover': { bgcolor: ACCENT, opacity: 0.9 } }}>
                    Continue to guest selection
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {/* Step 1: Guest selection */}
            {step === 1 ? (
              <Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={1} sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Notification queue ({selectedGuests.length} selected)
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Chip label="All" size="small" clickable color={segmentFilter === 'all' ? 'primary' : 'default'} onClick={() => setSegmentFilter('all')} sx={segmentFilter === 'all' ? { bgcolor: ACCENT, color: '#04120e' } : {}} />
                    {segments.filter((s) => s.count > 0).map((seg) => (
                      <Chip
                        key={seg.id}
                        label={`${seg.label} (${seg.count})`}
                        size="small"
                        clickable
                        onClick={() => setSegmentFilter(seg.id)}
                        sx={segmentFilter === seg.id ? { bgcolor: alpha(seg.color, 0.2), color: seg.color, fontWeight: 700, border: `1px solid ${alpha(seg.color, 0.4)}` } : {}}
                      />
                    ))}
                  </Stack>
                </Stack>

                <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                  <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1, bgcolor: isDark ? alpha('#fff', 0.04) : alpha(ACCENT, 0.06), borderBottom: 1, borderColor: 'divider' }}>
                    <Checkbox
                      size="small"
                      indeterminate={selectedGuests.length > 0 && selectedGuests.length < filteredGuests.length}
                      checked={filteredGuests.length > 0 && filteredGuests.every((g) => selected[g.id])}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>Guest</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, width: 100, display: { xs: 'none', md: 'block' } }}>Segment</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, width: 56, textAlign: 'right' }}>Score</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, width: 72, textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>Channels</Typography>
                  </Stack>
                  <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                    {filteredGuests.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                        No guests in this segment.
                      </Typography>
                    ) : (
                      filteredGuests.map((g) => {
                        const seg = segments.find((s) => s.id === g.segment_id);
                        const SegIcon = SEGMENT_ICONS[g.segment_id] || ReplayIcon;
                        return (
                          <Stack
                            key={g.id}
                            direction="row"
                            alignItems="center"
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom: 1,
                              borderColor: 'divider',
                              bgcolor: selected[g.id] ? (isDark ? alpha(ACCENT, 0.06) : alpha(ACCENT, 0.04)) : 'transparent',
                              '&:hover': { bgcolor: isDark ? alpha('#fff', 0.03) : alpha(ACCENT, 0.03) },
                            }}
                          >
                            <Checkbox
                              size="small"
                              checked={Boolean(selected[g.id])}
                              onChange={(e) => setSelected((prev) => ({ ...prev, [g.id]: e.target.checked }))}
                            />
                            <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{g.name}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                Last stay {g.last_booking} · {g.total_bookings} stay(s) · {g.currency} {(g.total_spent || 0).toLocaleString('en-IN')}
                              </Typography>
                            </Box>
                            <Chip
                              icon={<SegIcon sx={{ fontSize: '14px !important' }} />}
                              label={seg?.label || g.segment_label}
                              size="small"
                              sx={{
                                width: 100,
                                display: { xs: 'none', md: 'flex' },
                                fontSize: '0.68rem',
                                bgcolor: alpha(seg?.color || ACCENT, 0.12),
                                color: seg?.color || ACCENT,
                                '& .MuiChip-icon': { color: 'inherit' },
                              }}
                            />
                            <Typography variant="body2" sx={{ width: 56, textAlign: 'right', fontWeight: 800, color: ACCENT }}>
                              {g.retention_score}
                            </Typography>
                            <Stack direction="row" spacing={0.25} sx={{ width: 72, justifyContent: 'center', display: { xs: 'none', sm: 'flex' } }}>
                              {g.has_email ? <EmailOutlinedIcon sx={{ fontSize: 16, color: '#3388f2' }} /> : null}
                              {g.has_phone ? <WhatsAppIcon sx={{ fontSize: 16, color: '#25D366' }} /> : null}
                            </Stack>
                          </Stack>
                        );
                      })
                    )}
                  </Box>
                </Paper>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Message templates by segment</Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {segments.filter((s) => s.count > 0).map((seg) => (
                    <Grid item xs={12} key={seg.id}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label={`${seg.label} template`}
                        value={segmentTemplates[seg.id] || ''}
                        onChange={(e) => setSegmentTemplates((prev) => ({ ...prev, [seg.id]: e.target.value }))}
                        helperText="Variables: {guest_name}, {hotel_name}, {last_stay}, {total_bookings}, {offer_hint}"
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>

                <Stack direction="row" justifyContent="space-between">
                  <Button onClick={() => setStep(0)}>Back</Button>
                  <Button variant="contained" onClick={() => setStep(2)} disabled={!selectedGuests.length} sx={{ bgcolor: ACCENT, color: '#04120e', fontWeight: 700, '&:hover': { bgcolor: ACCENT, opacity: 0.9 } }}>
                    Preview messages
                  </Button>
                </Stack>
              </Box>
            ) : null}

            {/* Step 2: Preview & send */}
            {step === 2 ? (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Preview guest</Typography>
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Sample guest</InputLabel>
                      <Select
                        label="Sample guest"
                        value={previewGuest?.id || ''}
                        onChange={(e) => setPreviewGuestId(Number(e.target.value))}
                      >
                        {selectedGuests.map((g) => (
                          <MenuItem key={g.id} value={g.id}>{g.name} · {g.segment_label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {previewGuest ? (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, textTransform: 'uppercase' }}>Personalized message</Typography>
                        <Typography variant="body2" sx={{ mt: 1, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                          {messageForGuest(previewGuest)}
                        </Typography>
                      </Paper>
                    ) : null}

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Delivery channels ({selectedGuests.length} recipients)
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                      {canEmail ? (
                        <Button
                          size="small"
                          variant={channels.email ? 'contained' : 'outlined'}
                          startIcon={<EmailOutlinedIcon />}
                          onClick={() => setChannels((c) => ({ ...c, email: !c.email }))}
                          sx={channels.email ? { bgcolor: '#3388f2' } : {}}
                        >
                          Email
                        </Button>
                      ) : null}
                      {canWhatsApp ? (
                        <Button
                          size="small"
                          variant={channels.whatsapp ? 'contained' : 'outlined'}
                          startIcon={<WhatsAppIcon />}
                          onClick={() => setChannels((c) => ({ ...c, whatsapp: !c.whatsapp }))}
                          sx={channels.whatsapp ? { bgcolor: '#25D366' } : {}}
                        >
                          WhatsApp
                        </Button>
                      ) : null}
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', minHeight: 380 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={previewTab} onChange={(_, v) => setPreviewTab(v)} sx={{ minHeight: 44 }}>
                          <Tab value="email" label="Email preview" icon={<EmailOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }} />
                          <Tab value="whatsapp" label="WhatsApp preview" icon={<WhatsAppIcon sx={{ fontSize: 18 }} />} iconPosition="start" sx={{ minHeight: 44, textTransform: 'none', fontWeight: 700 }} />
                        </Tabs>
                        <Tooltip title="Refresh preview">
                          <IconButton size="small" onClick={refreshPreview} disabled={previewLoading}>
                            {previewLoading ? <CircularProgress size={18} /> : <PreviewIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {previewLoading ? <LinearProgress sx={{ bgcolor: alpha(ACCENT, 0.1), '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} /> : null}
                      <Box sx={{ p: previewTab === 'email' ? 0 : 2, minHeight: 320, bgcolor: previewTab === 'email' ? '#e8ecf0' : isDark ? alpha('#25D366', 0.06) : '#e8f5e9' }}>
                        {previewTab === 'email' ? (
                          emailPreviewHtml ? (
                            <Box
                              component="iframe"
                              title="Email preview"
                              srcDoc={emailPreviewHtml}
                              sx={{ width: '100%', height: 360, border: 0, display: 'block' }}
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Select a guest to preview email.</Typography>
                          )
                        ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              maxWidth: 320,
                              ml: 'auto',
                              mr: 2,
                              mt: 1,
                              p: 2,
                              borderRadius: 3,
                              bgcolor: '#fff',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            }}
                          >
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#111', lineHeight: 1.55, fontSize: '0.9rem' }}>
                              {whatsappPreview || 'Select a guest to preview WhatsApp.'}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#8696a0', textAlign: 'right' }}>
                              {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <Button onClick={() => setStep(1)} disabled={sending}>Back</Button>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip icon={<CheckCircleOutlineIcon />} label={`${selectedGuests.length} guests queued`} size="small" sx={{ fontWeight: 700 }} />
                    <AiGradientButton
                      disabled={sending || !selectedGuests.length}
                      onClick={handleSend}
                      startIcon={sending ? <CircularProgress size={18} color="inherit" /> : undefined}
                      showSparkle={!sending}
                    >
                      {sending ? 'Sending…' : 'Send retention campaign'}
                    </AiGradientButton>
                  </Stack>
                </Stack>
              </Box>
            ) : null}
          </>
        )}
      </Box>

      {!isPage && onClose ? (
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button fullWidth variant="outlined" onClick={onClose} disabled={sending}>Close</Button>
        </Box>
      ) : null}
    </Box>
  );
}

export default AiRetentionPanel;
